/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */







(function () {
function scrollToReviews() {
var target = document.querySelector('.jdgm-review-widget');
if (!target) return;
var headerOffset = 120; // adjust if your header is taller
var elementPosition = target.getBoundingClientRect().top;
var offsetPosition = elementPosition + window.pageYOffset - headerOffset;
window.scrollTo({
top: offsetPosition,
behavior: 'smooth'
});
}
function attachClick() {
var trigger = document.querySelector('.jdgm-all-reviews-text');
if (!trigger || trigger.dataset.jdgmScrollBound === "1") return false;
trigger.addEventListener('click', function (e) {
e.preventDefault();
scrollToReviews();
});
trigger.dataset.jdgmScrollBound = "1";
return true;
}
window.addEventListener('load', function () {
var tries = 0;
var maxTries = 40;
var interval = setInterval(function () {
tries++;
if (attachClick() || tries >= maxTries) {
clearInterval(interval);
}
}, 250);
new MutationObserver(attachClick).observe(document.body, {
childList: true,
subtree: true
});
});
})();




(function () {
  const STICKY_ID = 'sticky-atc';

  function findMainProductForm() {
    const scope =
      document.querySelector('main [data-section-type="main-product"]') ||
      document.querySelector('main .product') ||
      document.querySelector('main') ||
      document;

    if (!scope) return null;

    return (
      scope.querySelector('product-form form[action*="/cart/add"]') ||
      scope.querySelector('form[action*="/cart/add"]')
    );
  }

  function initStickyATC() {
    let sticky = document.getElementById(STICKY_ID);

    const productForm = findMainProductForm();
    if (!productForm) return;

    const originalBtn = productForm.querySelector('button[name="add"], button[type="submit"]');
    if (!originalBtn) return;

    if (!sticky) {
      sticky = document.createElement('div');
      sticky.id = STICKY_ID;
      sticky.className = 'sticky-atc';
      sticky.innerHTML = `<div class="sticky-atc__inner"></div>`;
      document.body.appendChild(sticky);
    }

    let stickyBtn = sticky.querySelector('.sticky-atc__button');

    if (!stickyBtn) {
      stickyBtn = originalBtn.cloneNode(true);
      stickyBtn.classList.add('sticky-atc__button');

      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (originalBtn.disabled) return;
        originalBtn.click();
      });

      sticky.querySelector('.sticky-atc__inner').appendChild(stickyBtn);
    }

    const sync = () => {
      const latestForm = findMainProductForm();
      const latestOriginalBtn = latestForm
        ? latestForm.querySelector('button[name="add"], button[type="submit"]')
        : null;

      const sourceBtn = latestOriginalBtn || originalBtn;
      if (!sourceBtn) return;

      if (stickyBtn.textContent !== sourceBtn.textContent) {
        stickyBtn.textContent = sourceBtn.textContent;
      }

      stickyBtn.disabled = sourceBtn.disabled;
      stickyBtn.setAttribute(
        'aria-disabled',
        sourceBtn.getAttribute('aria-disabled') || (sourceBtn.disabled ? 'true' : 'false')
      );
    };

    sync();

    const btnObs = new MutationObserver(sync);
    btnObs.observe(originalBtn, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true
    });

    const io = new IntersectionObserver(([entry]) => {
      const shouldShow = !entry.isIntersecting;
      sticky.classList.toggle('is-visible', shouldShow);
      document.body.classList.toggle('has-sticky-atc', shouldShow);
    }, { threshold: 0.01 });

    io.observe(originalBtn);

    return true;
  }

  function boot() {
    initStickyATC();

    const mo = new MutationObserver(() => {
      initStickyATC();
    });

    mo.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    document.addEventListener('on:variant:change', () => {
      setTimeout(initStickyATC, 50);
      setTimeout(initStickyATC, 250);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();








(() => {
  const root = document.querySelector('.pq-carousel');
  if (!root) return;

  const viewport = root.querySelector('.pq-carousel__viewport');
  const track = root.querySelector('[data-track]');
  const prevBtn = root.querySelector('.pq-carousel__btn--prev');
  const nextBtn = root.querySelector('.pq-carousel__btn--next');
  const dotButtons = Array.from(root.querySelectorAll('.pq-dot'));

  const originalSlides = Array.from(track.querySelectorAll('[data-slide]'));
  const originalCount = originalSlides.length;

  // Clone first/last sets for seamless looping
  const clonesBefore = originalSlides.map(s => s.cloneNode(true));
  const clonesAfter  = originalSlides.map(s => s.cloneNode(true));
  clonesBefore.forEach(c => track.insertBefore(c, track.firstChild));
  clonesAfter.forEach(c => track.appendChild(c));

  const allSlides = Array.from(track.querySelectorAll('[data-slide]'));

let index = originalCount + 1; // start on second original (prevents same-on-left at load)

  let isAnimating = false;

  function setCenterClasses() {
    allSlides.forEach(s => s.classList.remove('is-center', 'is-side'));

    const center = allSlides[index];
    const left = allSlides[index - 1];
    const right = allSlides[index + 1];

    if (center) center.classList.add('is-center');
    if (left) left.classList.add('is-side');
    if (right) right.classList.add('is-side');

    const logical = ((index - originalCount) % originalCount + originalCount) % originalCount;
    dotButtons.forEach((b, i) => b.classList.toggle('is-active', i === logical));
  }

  function getSlideMetrics() {
    const first = allSlides[0];
    if (!first) return { slideW: 0, gap: 0 };

    const slideW = first.getBoundingClientRect().width;

    const style = window.getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap || "0") || 0;

    return { slideW, gap };
  }

  function translateToIndex(instant = false) {
    const { slideW, gap } = getSlideMetrics();
    const step = slideW + gap;

    const vpStyles = window.getComputedStyle(viewport);
    const padL = parseFloat(vpStyles.paddingLeft) || 0;

    const vpW = viewport.getBoundingClientRect().width;

    // IMPORTANT: because track starts AFTER left padding, center needs -padL
    const centerX = (vpW / 2) - padL;

    const activeCenter = index * step + slideW / 2;
    const x = centerX - activeCenter;

    if (instant) {
      track.style.transition = "none";
      track.style.transform = `translate3d(${x}px,0,0)`;
      track.offsetHeight; // reflow
      track.style.transition = "";
    } else {
      track.style.transform = `translate3d(${x}px,0,0)`;
    }

    setCenterClasses();
  }

  function go(delta) {
    if (isAnimating) return;
    isAnimating = true;

    index += delta;
    translateToIndex(false);

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = prefersReduced ? 0 : 360;

    window.setTimeout(() => {
      if (index >= originalCount * 2) {
        index -= originalCount;
        translateToIndex(true);
      } else if (index < originalCount) {
        index += originalCount;
        translateToIndex(true);
      }

      isAnimating = false;
    }, duration + 30);
  }

  if (prevBtn) prevBtn.addEventListener('click', () => go(-1));
  if (nextBtn) nextBtn.addEventListener('click', () => go(1));

  dotButtons.forEach((btn, dotIndex) => {
    btn.addEventListener('click', () => {
      if (isAnimating) return;

      const logical = ((index - originalCount) % originalCount + originalCount) % originalCount;
      const delta = dotIndex - logical;
      if (delta === 0) return;

      go(delta);
    });
  });

  function init() { translateToIndex(true); }
  window.addEventListener('load', init);
  window.addEventListener('resize', () => translateToIndex(true));
  init();
})();




























(() => {
  const DRAWER_SELECTORS = [
    '#CartDrawer',
    'cart-drawer',
    '[data-cart-drawer]',
    '.cart-drawer'
  ];

  const OVERLAY_SELECTORS = [
    '.drawer-overlay',
    '.cart-overlay',
    '.modal-overlay',
    '.overlay'
  ];

  const body = document.body;

  const isVisible = (el) => {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  };

  const findFirst = (selectors) => {
    for (const s of selectors) {
      const el = document.querySelector(s);
      if (el) return el;
    }
    return null;
  };

  const drawerLooksOpen = () => {
    const drawer = findFirst(DRAWER_SELECTORS);
    if (!drawer) return false;

    const openByAttr =
      drawer.hasAttribute('open') ||
      drawer.getAttribute('aria-hidden') === 'false' ||
      drawer.classList.contains('is-open') ||
      drawer.classList.contains('active') ||
      drawer.classList.contains('open');

    if (isVisible(drawer) && openByAttr) return true;
    if (isVisible(drawer) && drawer.getAttribute('aria-hidden') !== 'true') return true;

    return false;
  };

  const overlayLooksPresent = () => {
    const overlay = findFirst(OVERLAY_SELECTORS);
    return !!overlay && isVisible(overlay);
  };

  const unlockBody = () => {
    body.classList.remove('fixed', 'no-scroll', 'scroll-lock', 'overflow-hidden');
    document.documentElement.classList.remove('fixed', 'no-scroll', 'scroll-lock', 'overflow-hidden');

    if (body.style.overflow === 'hidden') body.style.overflow = '';
    if (document.documentElement.style.overflow === 'hidden') document.documentElement.style.overflow = '';

    // Leave overlay handling to your CSS (so it doesn't "come back" via JS)
    // (We won't force-hide any overlay here)
  };

  const fixIfClosed = () => {
    if (!drawerLooksOpen()) {
      if (body.classList.contains('fixed') || overlayLooksPresent()) {
        unlockBody();
      }
    }
  };

  const observer = new MutationObserver(() => {
    requestAnimationFrame(fixIfClosed);
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'style', 'aria-hidden', 'open'],
    subtree: true,
    childList: true
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') setTimeout(fixIfClosed, 0);
  });

  document.addEventListener('click', () => {
    setTimeout(fixIfClosed, 0);
  });

  fixIfClosed();
})();












































































(() => {
  const AXE_LIST_SELECTOR = '.axe-checkbox-upsell-list';
  const PRICE_SELECTOR = '.product-price .price__default .price__current';

  const parseMoneyToCents = (text) => {
    const cleaned = (text || '').replace(/[^0-9.,]/g, '').trim();
    if (!cleaned) return null;
    const normalized = cleaned.includes('.') ? cleaned.replace(/,/g, '') : cleaned.replace(',', '.');
    const value = Number(normalized);
    if (Number.isNaN(value)) return null;
    return Math.round(value * 100);
  };

const formatMoney = (cents) => {
  const currency =
    (window.Shopify && Shopify.currency && Shopify.currency.active) ||
    document.documentElement.getAttribute('data-currency') ||
    'EUR';

  // Force an 'en' locale so symbol is typically leading; still not 100% for every currency
  return new Intl.NumberFormat('en', { style: 'currency', currency }).format(cents / 100);
};


  const getAxeState = () => {
  const axeList = document.querySelector(AXE_LIST_SELECTOR);
  if (!axeList) return { exists: false, checked: false, upsellCents: 0 };

  const checkboxes = [...axeList.querySelectorAll('.axe-input[type="checkbox"]')];
  if (!checkboxes.length) return { exists: true, checked: false, upsellCents: 0 };

  let upsellCents = 0;
  let anyChecked = false;

  for (const cb of checkboxes) {
    if (!cb.checked) continue;
    anyChecked = true;

    const checkboxId = cb.getAttribute('data-checkbox-id') || cb.getAttribute('data-id');

    // Prefer matching debug div by checkbox id (axe-debug-info-1/2/3...)
    let found = 0;
    if (checkboxId) {
      const debugDiv = axeList.querySelector(`.axe-debug-info-1[data-axe-checkbox-id="${checkboxId}"],
                                             .axe-debug-info-2[data-axe-checkbox-id="${checkboxId}"],
                                             .axe-debug-info-3[data-axe-checkbox-id="${checkboxId}"],
                                             [class^="axe-debug-info-"][data-axe-checkbox-id="${checkboxId}"]`);
      if (debugDiv) {
        try {
          const obj = JSON.parse((debugDiv.textContent || '').trim());
          if (obj && typeof obj.price === 'number') found = obj.price; // cents
        } catch {}
      }
    }

    // Fallback: price text within the same upsell row
    if (!found) {
      const row = cb.closest('.axe-main');
      const txt = row?.querySelector('.axe-price')?.textContent;
      found = parseMoneyToCents(txt) || 0;
    }

    upsellCents += found;
  }

  return { exists: true, checked: anyChecked, upsellCents };
};


  const getPriceEl = () => document.querySelector(PRICE_SELECTOR);

  // Prevent observer feedback loops when we write textContent
  let isWriting = false;

  // Debounce to one update per frame
  let raf = null;
  const schedule = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = null;
      apply();
    });
  };

  const captureBaseIfNeeded = (priceEl) => {
    if (!priceEl) return;

    // Only capture base when theme is showing the real price (not our computed)
    if (priceEl.dataset.axeComputed === '1') return;

    const cents = parseMoneyToCents(priceEl.textContent);
    if (cents != null) priceEl.dataset.axeBaseCents = String(cents);
  };

  const apply = () => {
    const priceEl = getPriceEl();
    if (!priceEl) return;

    captureBaseIfNeeded(priceEl);

    const baseCentsRaw = priceEl.dataset.axeBaseCents;
    const baseCents = baseCentsRaw ? Number(baseCentsRaw) : parseMoneyToCents(priceEl.textContent);
    if (baseCents == null) return;

    const { exists, checked, upsellCents } = getAxeState();
    const shouldCompute = exists && checked && (upsellCents || 0) > 0;

    const finalCents = shouldCompute ? (baseCents + upsellCents) : baseCents;
    const finalText = formatMoney(finalCents);


    // If already correct, do nothing (cuts down work a lot)
    const currentText = (priceEl.textContent || '').trim();
    if (currentText === finalText) {
      priceEl.dataset.axeComputed = shouldCompute ? '1' : '0';
      return;
    }

    // Write once, guarded
    isWriting = true;
    priceEl.dataset.axeComputed = shouldCompute ? '1' : '0';
    priceEl.textContent = finalText;
    isWriting = false;
  };

  const bind = () => {
    const priceEl = getPriceEl();
    if (!priceEl) return;

    // Initial base capture + paint
    captureBaseIfNeeded(priceEl);
    schedule();

    // 1) Axe checkbox toggles (delegated so it survives re-renders)
    document.addEventListener('change', (e) => {
      if (e.target && e.target.matches(`${AXE_LIST_SELECTOR} .axe-input[type="checkbox"]`)) {
        schedule();
      }
    });

    // 2) Enterprise variant changes
    document.addEventListener('on:variant:change', () => {
      // theme may rewrite price after this event; schedule twice to catch it
      schedule();
      setTimeout(schedule, 0);
    });

    // 3) Watch ONLY the price element for theme rewrites
    const obs = new MutationObserver(() => {
      if (isWriting) return; // ignore our own changes
      // Theme rewrote the visible price; treat it as base, then re-apply if upsell checked
      captureBaseIfNeeded(priceEl);
      schedule();
    });

    obs.observe(priceEl, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();































(function () {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const MOBILE_MQ = '(max-width: 639px)';
  const INTERVAL_MS = 4000;
  const STEP_ITEMS = 2;

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  function getGapPx(gridEl) {
    const cs = window.getComputedStyle(gridEl);
    const gap = cs.columnGap || cs.gap || '0px';
    const val = parseFloat(gap);
    return Number.isFinite(val) ? val : 0;
  }

  function setup(carouselEl) {
    const slider = carouselEl.querySelector('.slider');
    const grid = carouselEl.querySelector('.slider__grid');
    if (!slider || !grid) return;

    // Prevent double init
    if (carouselEl.dataset.autoLoopInit === '1') return;
    carouselEl.dataset.autoLoopInit = '1';

    const items = Array.from(grid.children).filter((n) => n.classList.contains('slider__item'));
    if (items.length < 2) return;

    // Clone slides once (for seamless looping)
    // Only clone if not already cloned
    if (grid.dataset.cloned !== '1') {
      const frag = document.createDocumentFragment();
      items.forEach((li) => frag.appendChild(li.cloneNode(true)));
      grid.appendChild(frag);
      grid.dataset.cloned = '1';
    }

    // Measure step size: 1 item width + gap
    const first = grid.querySelector('.slider__item');
    const gap = getGapPx(grid);
    const itemW = first ? first.getBoundingClientRect().width : 0;
    if (!itemW) return;

    const stepPx = (itemW + gap) * STEP_ITEMS;

    // Compute original (pre-clone) scroll width
    // We can approximate by half of the current scrollWidth because we duplicated once.
    const originalScrollWidth = Math.floor(slider.scrollWidth / 2);

    let timer = null;
    let isInteracting = false;

    const hardWrapIfNeeded = async () => {
      // If we've moved into the cloned half, wrap back by subtracting original width.
      if (slider.scrollLeft >= originalScrollWidth) {
        const prev = slider.style.scrollBehavior;
        slider.style.scrollBehavior = 'auto';
        slider.scrollLeft = slider.scrollLeft - originalScrollWidth;
        await sleep(0);
        slider.style.scrollBehavior = prev;
      }
    };

    const tick = async () => {
      if (isInteracting) return;

      // Wrap first if needed (keeps us in the first half before moving)
      await hardWrapIfNeeded();

      // Smooth move right by 2 items
      slider.scrollBy({ left: stepPx, behavior: 'smooth' });

      // After the smooth scroll finishes, wrap if we ended up in cloned half
      await sleep(500);
      await hardWrapIfNeeded();
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(tick, INTERVAL_MS);
    };

    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    const pause = () => { isInteracting = true; stop(); };
    const resume = () => { isInteracting = false; start(); };

    slider.addEventListener('touchstart', pause, { passive: true });
    slider.addEventListener('touchend', resume);
    slider.addEventListener('pointerdown', pause);
    slider.addEventListener('pointerup', resume);

    document.addEventListener('visibilitychange', () => {
      document.hidden ? stop() : start();
    });

    // Start/stop only when visible
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? start() : stop()));
      }, { threshold: 0.25 });
      io.observe(carouselEl);
    } else {
      start();
    }
  }

  function init() {
    if (!window.matchMedia || !window.matchMedia(MOBILE_MQ).matches) return;
    document.querySelectorAll('carousel-slider.carousel').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.matchMedia) {
    const mq = window.matchMedia(MOBILE_MQ);
    mq.addEventListener?.('change', () => init());
  }
})();










































(() => {
  const CART_ROOT = '#lcu-llama-cart';
  const DIALOG_SEL = `${CART_ROOT} .lcu__drawer__content[role="dialog"]`;
  const CLOSE_BTN_SEL = `${CART_ROOT} .lcu__drawer__btn-close, ${CART_ROOT} [aria-label*="Close"], ${CART_ROOT} .lcu__backdrop`;

  let locked = false;
  let scrollY = 0;

  const getDialog = () => document.querySelector(DIALOG_SEL);

  // “Open” = dialog exists and aria-hidden is explicitly false
  const isOpen = () => {
    const d = getDialog();
    if (!d) return false;
    const ariaHidden = d.getAttribute('aria-hidden');
    return ariaHidden === 'false';
  };

  const lock = () => {
    if (locked) return;
    locked = true;

    scrollY = window.scrollY || 0;

    // Save previous inline styles
    const b = document.body;
    b.dataset.prevPosition = b.style.position || '';
    b.dataset.prevTop = b.style.top || '';
    b.dataset.prevLeft = b.style.left || '';
    b.dataset.prevRight = b.style.right || '';
    b.dataset.prevWidth = b.style.width || '';
    b.dataset.prevOverflow = b.style.overflow || '';

    b.style.position = 'fixed';
    b.style.top = `-${scrollY}px`;
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
  };

  const unlock = () => {
    if (!locked) return;
    locked = false;

    const b = document.body;

    b.style.position = b.dataset.prevPosition || '';
    b.style.top = b.dataset.prevTop || '';
    b.style.left = b.dataset.prevLeft || '';
    b.style.right = b.dataset.prevRight || '';
    b.style.width = b.dataset.prevWidth || '';
    b.style.overflow = b.dataset.prevOverflow || '';

    delete b.dataset.prevPosition;
    delete b.dataset.prevTop;
    delete b.dataset.prevLeft;
    delete b.dataset.prevRight;
    delete b.dataset.prevWidth;
    delete b.dataset.prevOverflow;

    window.scrollTo(0, scrollY);
  };

  const apply = () => {
    if (isOpen()) lock();
    else unlock();
  };

  // 1) Force unlock immediately on any close interaction (covers “first close” misses)
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target;
      if (t && t.closest && t.closest(CLOSE_BTN_SEL)) {
        // Llama may close async, so unlock now + recheck shortly after
        unlock();
        setTimeout(apply, 50);
        setTimeout(apply, 200);
      }
    },
    true // capture so we run even if Llama stops propagation
  );

  // 2) ESC key close
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape') {
        unlock();
        setTimeout(apply, 50);
        setTimeout(apply, 200);
      }
    },
    true
  );

  // 3) MutationObserver: watch for aria-hidden changes, and cart node replacements/removals
  const obs = new MutationObserver(() => apply());
  obs.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'class'],
    childList: true,
  });

  // 4) Safety: if cart root gets removed/replaced, unlock
  const removalObs = new MutationObserver(() => {
    const cart = document.querySelector(CART_ROOT);
    if (!cart) unlock();
  });
  removalObs.observe(document.body, { childList: true, subtree: true });

  // 5) Extra safety on navigation / backgrounding
  window.addEventListener('pagehide', unlock, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') unlock();
  });

  // Initial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
})();













































(() => {
  const HEADING_CLASS = 'lcu-i18n-heading';
  const LIST_SELECTOR = '#lcu-llama-cart .lcu__small .lcu__list';
  const NO_PAD_CLASS = 'lcu-no-bottom-padding';
  const HEADING_TEXT = '🔥 Exclusive Offers';

  const isVisible = (el) =>
    !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));

  const apply = () => {
    const lists = [...document.querySelectorAll(LIST_SELECTOR)].filter(isVisible);
    if (!lists.length) return;

    // Remove any injected headings everywhere
    document.querySelectorAll(`#lcu-llama-cart h2.${HEADING_CLASS}`).forEach(h => h.remove());

    // Remove padding class everywhere (we will re-assign correctly below)
    lists.forEach(l => l.classList.remove(NO_PAD_CLASS));

    // Inject heading into FIRST visible list only, and remove duplicate native headings in that list
    const first = lists[0];
    first.querySelectorAll('h2.app__heading.lcu__list__heading').forEach(h => h.remove());

    const h = document.createElement('h2');
    h.className = `app__heading lcu__list__heading ${HEADING_CLASS}`;
    h.style.display = 'block';
    h.textContent = HEADING_TEXT;
    first.insertBefore(h, first.firstChild);

    // ✅ Only remove bottom padding when there are 2+ upsell blocks
    if (lists.length >= 2) {
      first.classList.add(NO_PAD_CLASS);
    }
  };

  let raf = null;
  const schedule = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = null;
      apply();
    });
  };

  const bind = () => {
    schedule();
    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });

    // Llama often rerenders after clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('.lcu__cta-button, .lcu__drawer__btn-remove, .lcu__button--no-style')) {
        setTimeout(schedule, 0);
        setTimeout(schedule, 150);
        setTimeout(schedule, 350);
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();









































/* AXE upsell: replace popup with inline dropdown UNDER the whole upsell (below image+text)
   - Smooth open/close animation
   - Only one panel open at a time
   - Hard-disables Axe popup trigger on the info icon
*/
(function () {
  const READY_VERSION = "3"; // bump this if you update code again

  // Track the single currently-open dropdown
  let currentlyOpen = null; // { inline, row, icon, close }

  function setupAxeInlineDropdown(root = document) {
    const icons = root.querySelectorAll('[id^="axe-popup-icon-"]');

    icons.forEach((icon) => {
      if (icon.dataset.axeDropdownReady === READY_VERSION) return;
      icon.dataset.axeDropdownReady = READY_VERSION;

      const idMatch = icon.id.match(/axe-popup-icon-(\d+)/);
      if (!idMatch) return;
      const n = idMatch[1];

      const overlay = document.getElementById(`axe-popup-overlay-${n}`);
      const popup = document.getElementById(`axe-popup-${n}`);
      if (!popup) return;

      // --- HARD disable original Axe popup trigger on the icon ---
      icon.removeAttribute("onclick");
      icon.removeAttribute("href");

      const row = icon.closest(".axe-text-main-content") || icon.parentElement;

      // Reuse existing inline block if already there
      let inline =
        (icon.closest(".axe-checkbox-upsell-item") ||
          icon.closest(".axe-checkbox-upsell-list") ||
          icon.closest("label") ||
          row ||
          document).querySelector?.(`.axe-inline-info[data-axe-n="${n}"]`) || null;

      if (!inline) {
        inline = document.createElement("div");
        inline.className = "axe-inline-info";
        inline.dataset.axeN = n;

        // Clone popup contents
        const clone = popup.cloneNode(true);

        // Remove modal-y inline styles
        clone.style.display = "";
        clone.style.position = "";
        clone.style.top = "";
        clone.style.left = "";
        clone.style.transform = "";
        clone.style.padding = "";
        clone.style.minWidth = "";
        clone.style.backgroundColor = "";
        clone.style.color = "";

        // Remove the ✕ close button inside cloned content (optional)
        const closeBtn = clone.querySelector(
          'span[onclick^="window.closeAxeCheckboxPopup"]'
        );
        if (closeBtn) closeBtn.remove();

        inline.innerHTML = clone.innerHTML;

        // --- Insert dropdown BELOW THE WHOLE UPSELL (below image + text) ---
        const item =
          icon.closest(".axe-checkbox-upsell-item") ||
          icon.closest("label") ||
          row;

        if (item) item.insertAdjacentElement("afterend", inline);
        else (row || icon).insertAdjacentElement("afterend", inline);
      }

      // Disable original overlay/popup
      if (overlay) overlay.style.display = "none";
      popup.style.display = "none";

      // ----- Animation + state helpers -----
      function setOpenState(isOpen) {
        inline.classList.toggle("is-open", isOpen);
        icon.classList.toggle("is-open", isOpen);
        if (row) row.classList.toggle("axe-open", isOpen); // chevron rotates via CSS
      }

      function animateOpen() {
        // Only one open at a time
        if (currentlyOpen && currentlyOpen.inline !== inline) {
          currentlyOpen.close();
        }

        setOpenState(true);

        // Start collapsed
        inline.style.maxHeight = "0px";
        inline.style.opacity = "0";

        // Expand to exact height
        requestAnimationFrame(() => {
          const target = inline.scrollHeight || 0;
          inline.style.maxHeight = target + "px";
          inline.style.opacity = "1";
        });

        // After expand, allow natural height growth
        const onEnd = (e) => {
          if (e.propertyName !== "max-height") return;
          inline.style.maxHeight = "none";
          inline.removeEventListener("transitionend", onEnd);
        };
        inline.addEventListener("transitionend", onEnd);

        currentlyOpen = { inline, row, icon, close: animateClose };
      }

      function animateClose() {
        if (!inline.classList.contains("is-open")) return;

        // Lock current height (if it was 'none')
        const start =
          inline.scrollHeight || inline.getBoundingClientRect().height || 0;
        inline.style.maxHeight = start + "px";
        inline.style.opacity = "1";

        requestAnimationFrame(() => {
          inline.style.maxHeight = "0px";
          inline.style.opacity = "0";
        });

        const onEnd = (e) => {
          if (e.propertyName !== "max-height") return;
          setOpenState(false);
          inline.removeEventListener("transitionend", onEnd);

          if (currentlyOpen && currentlyOpen.inline === inline) {
            currentlyOpen = null;
          }
        };
        inline.addEventListener("transitionend", onEnd);
      }

      function toggle() {
        if (inline.classList.contains("is-open")) animateClose();
        else animateOpen();
      }

      // Capture-phase click handler to override any Axe handlers
      icon.addEventListener(
        "click",
        function (e) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          toggle();
        },
        true
      );

      // OPTIONAL: click the row to toggle (keeps checkbox working)
      if (row && !row.dataset.axeRowToggleReady) {
        row.dataset.axeRowToggleReady = "1";
        row.addEventListener(
          "click",
          function (e) {
            if (e.target.closest('input[type="checkbox"]')) return;
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            toggle();
          },
          true
        );
      }
    });

    // One outside-click listener (global)
    if (!document.documentElement.dataset.axeOutsideClickReady) {
      document.documentElement.dataset.axeOutsideClickReady = "1";
      document.addEventListener(
        "click",
        function (e) {
          if (!currentlyOpen) return;

          const { inline, icon, row, close } = currentlyOpen;

          if (inline.contains(e.target)) return;
          if (icon && icon.contains(e.target)) return;
          if (row && row.contains(e.target)) return;

          close();
        },
        true
      );
    }
  }

  document.addEventListener("DOMContentLoaded", () => setupAxeInlineDropdown());

  // Re-run on dynamic re-renders
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node && node.nodeType === 1) setupAxeInlineDropdown(node);
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();





































// Delivery WOWETA app. Code to hide 00d if no days left
document.addEventListener("DOMContentLoaded", function () {
  function cleanAllCountdowns() {
    document.querySelectorAll("[data-countdown-timer]").forEach(function (timer) {
      const text = timer.textContent.trim();

      // Remove leading 00d only
      const cleaned = text.replace(/^00d\s*/, "");

      if (text !== cleaned) {
        timer.textContent = cleaned;
      }

      // Show after cleaning
      timer.style.visibility = "visible";
    });
  }

  // Hide timers first to avoid flicker
  document.querySelectorAll("[data-countdown-timer]").forEach(function (timer) {
    timer.style.visibility = "hidden";
  });

  // Clean immediately
  cleanAllCountdowns();

  // Watch the whole document because the app may replace the timer element itself
  const observer = new MutationObserver(function () {
    cleanAllCountdowns();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });
});





// This is for WOWETA app and adds the Van icon on the left as the app doesn't support adding an icon
document.addEventListener("DOMContentLoaded", function () {
  function cleanCountdowns() {
    document.querySelectorAll("#wowedd-template [data-countdown-timer]").forEach(function (timer) {
      const text = timer.textContent.trim();
      const cleaned = text.replace(/^00d\s*/, "");
      if (text !== cleaned) {
        timer.textContent = cleaned;
      }
    });
  }

  function styleDeliveryMessage() {
    const message = document.querySelector("#wowedd-template .edd-template-message");
    if (!message) return;

    const p = message.querySelector("p");
    if (!p) return;

    // Only build layout once
    if (!p.querySelector(".edd-delivery-icon")) {
      const originalHTML = p.innerHTML;

      p.innerHTML = `
        <span class="edd-delivery-icon" aria-hidden="true">
          <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
            <path d="m493.719 282.317c-.047-1.794-.395-3.567-1.028-5.246l-46.993-124.313c-2.354-6.225-8.312-10.343-14.967-10.343h-71.349v-92.06c0-8.837-7.163-16-16-16h-312.382c-8.837 0-16 7.163-16 16v251.835 108.059c0 8.837 7.163 16 16 16h36.572c7.203 29.461 33.811 51.396 65.459 51.396s58.257-21.934 65.46-51.396h114.308c7.203 29.461 33.811 51.396 65.459 51.396s58.257-21.934 65.46-51.396h37.282c4.314 0 8.446-1.742 11.458-4.833 3.012-3.09 4.647-7.265 4.537-11.578zm-446.719-215.962h280.382v219.835h-280.382zm86.031 379.29c-19.517 0-35.395-15.878-35.395-35.396 0-19.517 15.878-35.395 35.395-35.395s35.395 15.878 35.395 35.395c0 19.518-15.878 35.396-35.395 35.396zm245.228 0c-19.517 0-35.395-15.878-35.395-35.396 0-19.517 15.878-35.395 35.395-35.395s35.395 15.878 35.395 35.395c-.001 19.518-15.879 35.396-35.395 35.396zm65.459-51.396c-7.203-29.461-33.811-51.395-65.459-51.395s-58.257 21.934-65.459 51.395h-114.309c-7.203-29.461-33.811-51.395-65.459-51.395s-58.257 21.934-65.459 51.395h-20.573v-76.059h296.382c8.837 0 16-7.163 16-16v-127.775h60.292l42.125 111.435 2.785 108.399z"></path>
          </svg>
        </span>
        <span class="edd-delivery-text">${originalHTML}</span>
      `;
    }

    cleanCountdowns();
  }

  // Initial run
  styleDeliveryMessage();

  // Watch for app updates/re-renders
  const observer = new MutationObserver(function () {
    styleDeliveryMessage();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });
});





































// Animated PDP stat circles

document.addEventListener("DOMContentLoaded", function () {

  const circles = document.querySelectorAll('.bg-circle');

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {

        const circle = entry.target;
        const score = parseFloat(circle.dataset.score);
        const progress = circle.querySelector('.bg-circle__progress');

        const radius = 52;
        const circumference = 2 * Math.PI * radius;

        progress.style.strokeDasharray = circumference;
        progress.style.strokeDashoffset = circumference;

        const offset = circumference - (score / 100) * circumference;

        // Small timeout makes animation smoother
        setTimeout(() => {
          progress.style.strokeDashoffset = offset;
        }, 150);

        observer.unobserve(circle); // Animate only once
      }
    });
  }, {
    threshold: 0.4
  });

  circles.forEach(circle => observer.observe(circle));

});


























// hide product price on product page if APO Product Options is on the product page. 

document.addEventListener('DOMContentLoaded', function () {
  function togglePriceVisibility() {
    const hasApo = document.querySelector('.mw-apo-container');
    const priceBlocks = document.querySelectorAll('.product-info__price');

    priceBlocks.forEach(function(priceBlock) {
      priceBlock.style.display = hasApo ? 'none' : '';
    });
  }

  togglePriceVisibility();

  const observer = new MutationObserver(function () {
    togglePriceVisibility();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});
















// Pre Order Progress Bar //

document.addEventListener('DOMContentLoaded', function () {
  const fills = document.querySelectorAll('.bg-preorder-progress__fill');

  if (!fills.length) return;

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;

      const el = entry.target;
      const percent = el.getAttribute('data-fill') || 0;

      // slight delay for smoother feel
      setTimeout(() => {
        el.style.width = percent + '%';
      }, 250);

      observer.unobserve(el); // run once only
    });
  }, {
    threshold: 0.4
  });

  fills.forEach(el => observer.observe(el));
});





















/* HOMEPAGE LIFESTYLE CRO CARDS */

(function () {
  function getIconSvg(icon) {
    const icons = {
      dumbbell:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6M7 7v10M17 7v10M20 9v6M7 12h10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      heart:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20s-7-4.35-7-10a4 4 0 0 1 7-2.4A4 4 0 0 1 19 10c0 5.65-7 10-7 10Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      flame:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3s2 2.4 2 5a3 3 0 0 1-6 0c0-1.4.6-2.8 1.8-4C6.9 5.4 5 8 5 11.2A7 7 0 0 0 12 18a7 7 0 0 0 7-6.8c0-4.2-3-6.8-5.4-8.2.2 1.1-.1 2.2-.8 3.1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      house:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 10.5 12 4l9 6.5M5 9.5V20h14V9.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      moon:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 14.5A8 8 0 1 1 9.5 5a6.5 6.5 0 1 0 9.5 9.5Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      infinity:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18.5 8A4.5 4.5 0 0 1 22 15.5c-2.7 2.7-6.3.7-10-3-3.7-3.7-7.3-5.7-10-3A4.5 4.5 0 0 0 8.5 16c2.7-2.7 6.3-.7 10 3 3.7 3.7 7.3 5.7 10 3A4.5 4.5 0 0 0 18.5 8Z" transform="translate(-3 -4)" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    };

    return icons[icon] || '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';
  }

  function initGoalsSection(section) {
    if (!section || section.dataset.bgGoalsInit === 'true') return;
    section.dataset.bgGoalsInit = 'true';

    const carousel = section.querySelector('[data-bg-goals-carousel]');
    const cards = Array.from(section.querySelectorAll('[data-bg-goals-card]'));
    const prevBtn = section.querySelector('[data-bg-goals-prev]');
    const nextBtn = section.querySelector('[data-bg-goals-next]');
    const dots = Array.from(section.querySelectorAll('.bg-goals-dot'));

    const overlay = section.querySelector('[data-bg-goals-overlay]');
    const overlayCloseButtons = Array.from(section.querySelectorAll('[data-bg-goals-close]'));
    const overlayPrev = section.querySelector('[data-bg-goals-overlay-prev]');
    const overlayNext = section.querySelector('[data-bg-goals-overlay-next]');
    const overlayImage = section.querySelector('[data-bg-goals-overlay-image]');
    const overlayLabel = section.querySelector('[data-bg-goals-overlay-label]');
    const overlayIcon = section.querySelector('[data-bg-goals-overlay-icon]');
    const overlayHeadline = section.querySelector('[data-bg-goals-overlay-headline]');
    const overlayBody = section.querySelector('[data-bg-goals-overlay-body]');
    const overlayQuoteName = section.querySelector('[data-bg-goals-overlay-quote-name]');
    const overlayQuoteRole = section.querySelector('[data-bg-goals-overlay-quote-role]');
    const overlayQuoteText = section.querySelector('[data-bg-goals-overlay-quote-text]');
    const overlayCta = section.querySelector('[data-bg-goals-overlay-cta]');

    if (!carousel || !cards.length) return;

    let activeIndex = 0;

    function clampIndex(index) {
      if (index < 0) return 0;
      if (index > cards.length - 1) return cards.length - 1;
      return index;
    }

    function updateDots(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    function updateArrowStates(index) {
      if (prevBtn) prevBtn.classList.toggle('is-disabled', carousel.scrollLeft <= 2);
      if (nextBtn) {
        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
        nextBtn.classList.toggle('is-disabled', carousel.scrollLeft >= maxScroll - 2);
      }
      if (overlayPrev) overlayPrev.classList.toggle('is-disabled', index === 0);
      if (overlayNext) overlayNext.classList.toggle('is-disabled', index === cards.length - 1);
    }

    function getCardStep() {
      if (!cards.length) return carousel.clientWidth * 0.8;

      const firstCard = cards[0];
      const secondCard = cards[1];

      if (secondCard) {
        return secondCard.offsetLeft - firstCard.offsetLeft;
      }

      return firstCard.getBoundingClientRect().width;
    }

    function scrollByStep(direction) {
      const step = getCardStep();
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;
      const nextLeft = Math.max(0, Math.min(maxScroll, carousel.scrollLeft + (step * direction)));

      carousel.scrollTo({
        left: nextLeft,
        behavior: 'smooth'
      });
    }

    function getClosestCardIndex() {
      const currentScroll = carousel.scrollLeft;

      let closestIndex = 0;
      let closestDistance = Infinity;

      cards.forEach(function (card, index) {
        const distance = Math.abs(currentScroll - card.offsetLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    function scrollToCard(index, behavior) {
      const safeIndex = clampIndex(index);
      const targetCard = cards[safeIndex];
      if (!targetCard) return;

      activeIndex = safeIndex;

      carousel.scrollTo({
        left: Math.max(0, targetCard.offsetLeft),
        behavior: behavior || 'smooth'
      });

      updateDots(activeIndex);
      updateArrowStates(activeIndex);
    }

    function renderOverlay(index) {
      const safeIndex = clampIndex(index);
      activeIndex = safeIndex;

      const card = cards[safeIndex];
      const data = card.dataset;

      if (overlayImage) {
        overlayImage.src = data.image || '';
        overlayImage.alt = data.headline || data.label || '';
      }

      if (overlayLabel) overlayLabel.textContent = data.label || '';
      if (overlayIcon) overlayIcon.innerHTML = getIconSvg(data.icon);
      if (overlayHeadline) overlayHeadline.textContent = data.headline || '';
      if (overlayBody) {
        overlayBody.innerHTML = data.body ? '<p>' + data.body.replace(/\n/g, '<br>') + '</p>' : '';
      }
      if (overlayQuoteName) overlayQuoteName.textContent = data.quoteName || '';

      if (overlayQuoteRole) {
        overlayQuoteRole.textContent = data.quoteRole || '';
        overlayQuoteRole.style.display = data.quoteRole ? '' : 'none';
      }

      if (overlayQuoteText) overlayQuoteText.textContent = data.quoteText || '';

      if (overlayCta) {
        overlayCta.textContent = data.ctaLabel || 'Learn More';
        if (data.ctaLink) {
          overlayCta.href = data.ctaLink;
          overlayCta.style.display = 'inline-flex';
        } else {
          overlayCta.removeAttribute('href');
          overlayCta.style.display = 'none';
        }
      }

      updateDots(activeIndex);
      updateArrowStates(activeIndex);
    }

function openOverlay(index) {
  if (!overlay) return;

  renderOverlay(index);
  overlay.hidden = false;

  requestAnimationFrame(function () {
    overlay.classList.add('is-open');
    document.body.classList.add('bg-goals-lock');
  });
}

function closeOverlay() {
  if (!overlay) return;

  overlay.classList.remove('is-open');
  document.body.classList.remove('bg-goals-lock');

  window.setTimeout(function () {
    if (!overlay.classList.contains('is-open')) {
      overlay.hidden = true;
    }
  }, 320);
}

cards.forEach(function (card, index) {
  const openBtn = card.querySelector('[data-bg-goals-open]');
  const cardMedia = card.querySelector('.bg-goals-card-media');

  if (openBtn) {
    openBtn.addEventListener('click', function (event) {
      event.stopPropagation();
      openOverlay(index);
    });
  }

  if (cardMedia) {
    cardMedia.addEventListener('click', function (event) {
      if (event.target.closest('[data-bg-goals-open]')) return;
      openOverlay(index);
    });
  }
});

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollByStep(-1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollByStep(1);
      });
    }

    if (overlayPrev) {
      overlayPrev.addEventListener('click', function () {
        renderOverlay(clampIndex(activeIndex - 1));
      });
    }

    if (overlayNext) {
      overlayNext.addEventListener('click', function () {
        renderOverlay(clampIndex(activeIndex + 1));
      });
    }

    overlayCloseButtons.forEach(function (button) {
      button.addEventListener('click', closeOverlay);
    });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        const index = parseInt(dot.getAttribute('data-dot-index'), 10);
        scrollToCard(index, 'smooth');
      });
    });

    let scrollTimer = null;

    carousel.addEventListener(
      'scroll',
      function () {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(function () {
          activeIndex = clampIndex(getClosestCardIndex());
          updateDots(activeIndex);
          updateArrowStates(activeIndex);
        }, 120);
      },
      { passive: true }
    );

    document.addEventListener('keydown', function (event) {
      if (!overlay || overlay.hidden) return;

      if (event.key === 'Escape') {
        closeOverlay();
      }

      if (event.key === 'ArrowLeft') {
        renderOverlay(activeIndex - 1);
      }

      if (event.key === 'ArrowRight') {
        renderOverlay(activeIndex + 1);
      }
    });

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            section.classList.add('is-visible');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(section);

    requestAnimationFrame(function () {
      carousel.scrollLeft = 0;
      activeIndex = 0;
      updateDots(activeIndex);
      updateArrowStates(activeIndex);
    });
  }

  function initAllGoalsSections() {
    const sections = document.querySelectorAll('[data-bg-goals-section]');
    sections.forEach(initGoalsSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGoalsSections);
  } else {
    initAllGoalsSections();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target.querySelector('[data-bg-goals-section]') || event.target;
    if (section && section.matches && section.matches('[data-bg-goals-section]')) {
      initGoalsSection(section);
    } else {
      initAllGoalsSections();
    }
  });
})();




















